import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import {
    FileText,
    Download,
    ArrowLeft,
    Shield,
    CheckCircle2,
    Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES, DOCUMENTS } from '../constants';

export const WhitepaperPage: React.FC = () => {
    const { t, language } = useLanguage();
    const [pdfError, setPdfError] = useState(false);

    return (
        <div className="bg-[#050508] text-white min-h-screen pt-32 pb-20">

            {/* Background Effects */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-orange-600/5 blur-[120px] rounded-full pointer-events-none"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                {/* Back Link */}
                <div className="mb-8">
                    <Link to={ROUTES.TOKEN} className="flex items-center text-gray-400 hover:text-white transition-colors">
                        <ArrowLeft size={20} className="mr-2" />
                        {t('nav.token')}
                    </Link>
                </div>

                {/* Header */}
                <AnimatedSection className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-orange-900/20 text-orange-400 border border-orange-500/30 px-4 py-2 rounded-full mb-6">
                        <Shield size={16} />
                        <span className="text-xs font-bold uppercase tracking-wider">MICA Compliant</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-white to-orange-400">
                        White Paper
                    </h1>

                    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                        {t('token.whitepaper.description')}
                    </p>

                    {/* Download Button */}
                    <a href={DOCUMENTS.WHITEPAPER_LATEST} download>
                        <Button className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-orange-600/20">
                            <Download className="mr-2" size={20} />
                            {t('token.whitepaper.downloadBtn')}
                        </Button>
                    </a>
                </AnimatedSection>

                {/* Key Features */}
                <AnimatedSection delay={100} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    <div className="bg-[#0B1120] border border-gray-800 p-6 rounded-2xl">
                        <div className="w-12 h-12 bg-orange-900/20 rounded-xl flex items-center justify-center text-orange-400 mb-4">
                            <Shield size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">MICA Compliant</h3>
                        <p className="text-sm text-gray-400">Conforme alle normative europee MiCA per crypto-asset</p>
                    </div>

                    <div className="bg-[#0B1120] border border-gray-800 p-6 rounded-2xl">
                        <div className="w-12 h-12 bg-blue-900/20 rounded-xl flex items-center justify-center text-blue-400 mb-4">
                            <CheckCircle2 size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Trasparenza Totale</h3>
                        <p className="text-sm text-gray-400">Documentazione completa su tokenomics e governance</p>
                    </div>

                    <div className="bg-[#0B1120] border border-gray-800 p-6 rounded-2xl">
                        <div className="w-12 h-12 bg-green-900/20 rounded-xl flex items-center justify-center text-green-400 mb-4">
                            <Globe size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Versione Inglese</h3>
                        <p className="text-sm text-gray-400">Documento ufficiale in lingua inglese (v5)</p>
                    </div>
                </AnimatedSection>

                {/* PDF Viewer */}
                <AnimatedSection delay={200}>
                    <div className="bg-[#0B1120] border border-gray-800 rounded-3xl overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-900/20 to-transparent border-b border-gray-800 p-6">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-orange-900/20 rounded-xl flex items-center justify-center text-orange-400">
                                        <FileText size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-white">CTC White Paper v5</h2>
                                        <p className="text-sm text-gray-400">MICA Compliant - English Version</p>
                                    </div>
                                </div>

                                <a href={DOCUMENTS.WHITEPAPER_LATEST} download>
                                    <Button variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-900/20">
                                        <Download size={18} className="mr-2" />
                                        Download PDF
                                    </Button>
                                </a>
                            </div>
                        </div>

                        {/* PDF Embed */}
                        <div className="relative w-full" style={{ height: '800px' }}>
                            {!pdfError ? (
                                <iframe
                                    src={`${DOCUMENTS.WHITEPAPER_LATEST}#view=FitH`}
                                    className="w-full h-full"
                                    title="CTC White Paper"
                                    onError={() => setPdfError(true)}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                                    <FileText size={64} className="text-gray-600 mb-4" />
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        Impossibile visualizzare il PDF
                                    </h3>
                                    <p className="text-gray-400 mb-6">
                                        Il tuo browser potrebbe non supportare la visualizzazione diretta dei PDF.
                                    </p>
                                    <a href={DOCUMENTS.WHITEPAPER_LATEST} download>
                                        <Button className="bg-orange-600 hover:bg-orange-500">
                                            <Download className="mr-2" size={20} />
                                            Scarica il White Paper
                                        </Button>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Additional Info */}
                <AnimatedSection delay={300} className="mt-12">
                    <div className="bg-gradient-to-r from-orange-900/10 to-transparent border border-orange-500/30 rounded-2xl p-8">
                        <h3 className="text-2xl font-bold text-white mb-4">
                            Perché leggere il White Paper?
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex gap-3">
                                <CheckCircle2 size={24} className="text-orange-400 flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="font-bold text-white mb-1">Visione Completa</h4>
                                    <p className="text-sm text-gray-400">
                                        Comprendi la visione a lungo termine e gli obiettivi del progetto CTC
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <CheckCircle2 size={24} className="text-orange-400 flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="font-bold text-white mb-1">Tokenomics Dettagliata</h4>
                                    <p className="text-sm text-gray-400">
                                        Analisi completa della distribuzione, utility e meccanismi del token
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <CheckCircle2 size={24} className="text-orange-400 flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="font-bold text-white mb-1">Roadmap Strategica</h4>
                                    <p className="text-sm text-gray-400">
                                        Timeline dettagliata delle milestone e sviluppi futuri
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <CheckCircle2 size={24} className="text-orange-400 flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="font-bold text-white mb-1">Conformità Normativa</h4>
                                    <p className="text-sm text-gray-400">
                                        Dettagli sulla compliance MICA e registrazione ESMA
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

            </div>
        </div>
    );
};
